import { Checkbox, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { frequency, payment_method, yesNo } from '../../../utils/constants/misc'
import {
  dateOperators,
  equalityOperators,
  multiselectOperators,
  textOperators
} from '../../../utils/constants/operators'

/** -------------------- INCOME -------------------- */
export const getIncomeColumns = ({ customers = [] }) => {
  const getCustomersFilter = () =>
    customers.map((customer) => ({
      name: customer.id?.toString(),
      label: customer.customer_name
    }))

  return [
    { name: 'periodYear', label: 'Année', isFilter: true, operators: dateOperators },
    { name: 'periodMonth', label: 'Mois', isFilter: true, operators: dateOperators },
    { name: 'periodYearMonth', label: 'Période', isFilter: true, operators: dateOperators },
    {
      name: 'date',
      label: 'Date',
      inputType: 'date',
      type: 'date',
      format: 'DD/MM/YYYY',
      isFilter: true,
      operators: dateOperators
    },
    {
      display: 'customer.customer_name',
      name: 'customer_id',
      label: 'Client',
      isFilter: true,
      inputType: 'number',
      valueEditorType: 'multiselect',
      values: getCustomersFilter(),
      operators: [...equalityOperators, ...multiselectOperators]
    },
    { name: 'invoice.invoice_number', label: 'N° Facture', isFilter: false, operators: textOperators },
    { name: 'amount', label: 'Montant', type: 'currency' },
    { name: 'description', label: 'Description' },
    {
      name: 'payment_method',
      mapping: (name) => payment_method.find((item) => item.name === name?.toString())?.label || name,
      label: 'Méthode payement',
      isFilter: true,
      values: payment_method,
      valueEditorType: 'multiselect',
      operators: [...equalityOperators, ...multiselectOperators]
    },
    {
      name: 'createdDateTime',
      label: 'Date Création',
      isFilter: true,
      inputType: 'datetime-local',
      type: 'date',
      format: 'DD/MM/YYYY HH:mm:ss',
      operators: dateOperators
    },
    {
      name: 'createdBy',
      label: 'Créé Par',
      isFilter: true,
      operators: [...equalityOperators, ...multiselectOperators]
    },
    {
      name: 'lastModDateTime',
      label: 'Date Modification',
      isFilter: true,
      inputType: 'datetime-local',
      type: 'date',
      format: 'DD/MM/YYYY HH:mm:ss',
      operators: dateOperators
    },
    {
      name: 'lastModBy',
      label: 'Modifié Par',
      isFilter: true,
      operators: [...equalityOperators, ...multiselectOperators]
    }
  ]
}

export const getIncomeFields = (user, mode, location) => {
  const getDefaultCustomer = () => location?.state?.context?.customer_id ?? null
  const isDialogMode = () => mode === 'dialog'

  return [
    [
      {
        name: 'customer_id',
        label: 'Client',
        type: 'select',
        required: true,
        defaultValue: getDefaultCustomer,
        readOnlyWhen: isDialogMode,
        options: {
          type: 'api',
          object: 'customers',
          value: 'customer_name',
          depends: 'invoice_id',
          returnField: 'id',
          returnFields: ['id'],
          filter: (data) =>
            data.customer_id && data.invoice_id
              ? { company_id: user.company_id, id: data.customer_id }
              : { company_id: user.company_id },
          subFilter: (data) => (data.invoice_id ? { id: data.invoice_id } : undefined)
        },
        fullWidth: true,
        validation: [{ rule: 'req', error: 'Client obligatoire' }]
      }
    ],
    [
      {
        name: 'invoice_id',
        label: 'N° Facture',
        readOnlyWhen: isDialogMode,
        type: 'select',
        required: true,
        depends: 'customer_id',
        options: {
          type: 'api',
          object: 'invoices',
          value: 'invoice_number',
          returnField: 'id',
          returnFields: ['id', 'customer_id'],
          filter: (data) => {
            const { customer_id, invoice_id } = data
            if (customer_id || invoice_id) {
              return {
                $or: [{ $and: { company_id: user.company_id, status: { $ne: '80' }, customer_id } }, { id: invoice_id }]
              }
            }
            return { company_id: user.company_id, status: { $ne: '80' } }
          }
        },
        fullWidth: true,
        validation: [{ rule: 'req', error: 'N° Facture obligatoire' }]
      }
    ],
    [
      {
        name: 'date',
        label: 'Date',
        type: 'date',
        required: true,
        fullWidth: true,
        validation: [{ rule: 'req', error: 'Date obligatoire' }]
      }
    ],
    [
      {
        name: 'amount',
        label: 'Montant',
        type: 'currency',
        required: true,
        fullWidth: true,
        defaultValue: {
          type: 'api',
          object: 'invoices',
          value: 'total_amount_net',
          trigger: 'invoice_id',
          filter: (data) => (data.invoice_id ? { company_id: user.company_id, id: data.invoice_id } : undefined)
        },
        validation: [{ rule: 'req', error: 'Montant obligatoire' }]
      }
    ],
    [
      {
        name: 'payment_method',
        label: 'Méthode paiement',
        type: 'select',
        required: true,
        options: { type: 'data', returnField: 'id', returnFields: ['id'], value: 'label', data: payment_method },
        fullWidth: true,
        validation: [{ rule: 'req', error: 'Méthode paiement obligatoire' }]
      }
    ],
    [
      {
        name: 'description',
        label: 'Description',
        required: true,
        type: 'text',
        fullWidth: true,
        validation: [{ rule: 'req', error: 'Description obligatoire' }]
      }
    ]
  ]
}

/** -------------------- OUTCOME -------------------- */
export const getOutcomeColumns = ({ suppliers = [], customers = [] }) => {
  const getSuppliersFilter = () => suppliers.map((s) => ({ name: s.id?.toString(), label: s.supplier_name }))
  const getCustomersFilter = () => customers.map((c) => ({ name: c.id?.toString(), label: c.customer_name }))

  return [
    { name: 'periodYear', label: 'Année', isFilter: true, operators: dateOperators },
    { name: 'periodMonth', label: 'Mois', isFilter: true, operators: dateOperators },
    { name: 'periodYearMonth', label: 'Période', isFilter: true, operators: dateOperators },
    {
      name: 'date',
      label: 'Date',
      inputType: 'date',
      type: 'date',
      format: 'DD/MM/YYYY',
      isFilter: true,
      operators: dateOperators
    },
    {
      display: 'supplier.supplier_name',
      name: 'supplier_id',
      label: 'Fournisseur',
      isFilter: true,
      inputType: 'number',
      valueEditorType: 'multiselect',
      values: getSuppliersFilter(),
      operators: [...equalityOperators, ...multiselectOperators]
    },
    {
      display: 'customer.customer_name',
      name: 'customer_id',
      label: 'Client',
      isFilter: true,
      inputType: 'number',
      valueEditorType: 'multiselect',
      values: getCustomersFilter(),
      operators: [...equalityOperators, ...multiselectOperators]
    },
    { name: 'invoice_number', label: 'N° Facture', isFilter: true, operators: textOperators },
    { name: 'amount', label: 'Montant', type: 'currency' },
    { name: 'description', label: 'Description' },
    {
      name: 'payment_method',
      mapping: (name) => payment_method.find((item) => item.name === name?.toString())?.label || name,
      label: 'Méthode payement',
      isFilter: true,
      values: payment_method,
      valueEditorType: 'multiselect',
      operators: [...equalityOperators, ...multiselectOperators]
    },
    {
      name: 'createdDateTime',
      label: 'Date Création',
      isFilter: true,
      inputType: 'datetime-local',
      type: 'date',
      format: 'DD/MM/YYYY HH:mm:ss',
      operators: dateOperators
    },
    {
      name: 'createdBy',
      label: 'Créé Par',
      isFilter: true,
      operators: [...equalityOperators, ...multiselectOperators]
    },
    {
      name: 'lastModDateTime',
      label: 'Date Modification',
      isFilter: true,
      inputType: 'datetime-local',
      type: 'date',
      format: 'DD/MM/YYYY HH:mm:ss',
      operators: dateOperators
    },
    {
      name: 'lastModBy',
      label: 'Modifié Par',
      isFilter: true,
      operators: [...equalityOperators, ...multiselectOperators]
    }
  ]
}

export const getOutcomeFields = (mode, user, location) => {
  const getNextInvoiceDate = (data) => {
    if (!data.is_recurent || !data.recure_every || !data.date) return ''
    const date = dayjs(data.date).add(
      data.recure_every === 1 ? 1 : data.recure_every === 2 ? 1 : data.recure_every === 3 ? 3 : 1,
      data.recure_every === 4 ? 'year' : data.recure_every === 1 ? 'week' : 'month'
    )
    return date.format('YYYY-MM-DD')
  }

  const isDialogMode = () => mode === 'dialog'

  return [
    [
      {
        name: 'type',
        label: 'Type',
        type: 'select',
        required: true,
        readOnlyWhen: isDialogMode,
        options: {
          type: 'data',
          returnField: 'id',
          returnFields: ['id'],
          value: 'name',
          data: [
            { id: 1, name: 'Client' },
            { id: 2, name: 'Fournisseur' }
          ]
        },
        fullWidth: true,
        validation: [{ rule: 'req', error: 'Type obligatoire' }]
      }
    ],
    [
      {
        name: 'supplier_id',
        label: 'Fournisseur',
        isDisplay: (data) => data.type === 2,
        type: 'select',
        defaultValue: () => location?.state?.context?.supplier_id ?? null,
        required: true,
        options: { type: 'api', object: 'suppliers', value: 'supplier_name', returnField: 'id', returnFields: ['id'] },
        fullWidth: true,
        validation: [{ rule: 'req', error: 'Fournisseur obligatoire' }]
      }
    ],
    [
      {
        name: 'customer_id',
        label: 'Client',
        isDisplay: (data) => data.type === 1,
        type: 'select',
        defaultValue: () => location?.state?.context?.customer_id ?? null,
        required: true,
        options: { type: 'api', object: 'customers', value: 'customer_name', returnField: 'id', returnFields: ['id'] },
        fullWidth: true,
        validation: [{ rule: 'req', error: 'Client obligatoire' }]
      }
    ],
    [
      {
        name: 'invoice_number',
        label: 'N° Facture',
        isDisplay: (data) => data.type === 2,
        type: 'text',
        fullWidth: true,
        required: true,
        validation: [{ rule: 'req', error: 'N° Facture obligatoire' }]
      }
    ],
    [
      {
        name: 'creditNote_id',
        label: 'N° Avoir',
        isDisplay: (data) => data.type === 1,
        type: 'select',
        required: true,
        depends: 'customer_id',
        options: {
          type: 'api',
          object: 'creditNotes',
          value: 'creditNote_number',
          returnField: 'id',
          returnFields: ['id', 'customer_id']
        },
        fullWidth: true,
        validation: [{ rule: 'req', error: 'N° Avoir obligatoire' }]
      }
    ],
    [
      {
        name: 'date',
        label: 'Date',
        type: 'date',
        required: true,
        isDisplay: (data) => data.type != null,
        fullWidth: true,
        validation: [{ rule: 'req', error: 'Date obligatoire' }]
      }
    ],
    [
      {
        name: 'amount',
        label: 'Montant',
        type: 'currency',
        required: true,
        isDisplay: (data) => data.type != null,
        fullWidth: true,
        defaultValue: {
          type: 'api',
          object: 'creditNotes',
          value: 'total_amount_net',
          trigger: 'creditNote_id',
          filter: (data) => (data.creditNote_id ? { company_id: user.company_id, id: data.creditNote_id } : undefined)
        },
        validation: [{ rule: 'req', error: 'Montant obligatoire' }]
      }
    ],
    [
      {
        name: 'payment_method',
        label: 'Méthode paiement',
        type: 'select',
        required: true,
        isDisplay: (data) => data.type != null,
        options: { type: 'data', returnField: 'id', returnFields: ['id'], value: 'label', data: payment_method },
        fullWidth: true,
        validation: [{ rule: 'req', error: 'Méthode paiement obligatoire' }]
      }
    ],
    [
      {
        name: 'description',
        label: 'Description',
        type: 'text',
        required: true,
        isDisplay: (data) => data.type != null,
        fullWidth: true,
        validation: [{ rule: 'req', error: 'Description obligatoire' }]
      }
    ],
    [
      {
        name: 'is_recurent',
        label: 'Récurant',
        type: 'checkbox',
        isDisplay: (data) => data.type === 2,
        fullWidth: true
      }
    ],
    [
      {
        name: 'recure_every',
        label: 'Tout les',
        type: 'select',
        required: true,
        isDisplay: (data) => data.is_recurent,
        options: { type: 'data', returnField: 'id', returnFields: ['id'], value: 'name', data: frequency },
        fullWidth: true,
        validation: [{ rule: 'req', error: 'Tout les obligatoire' }]
      }
    ],
    [
      {
        name: 'nextDate',
        label: 'Prochaine facturation',
        type: 'date',
        readOnly: true,
        isDisplay: (data) => data.is_recurent,
        defaultValueFunction: getNextInvoiceDate,
        fullWidth: true
      }
    ]
  ]
}

/** -------------------- TAX -------------------- */
export const getTaxColumns = () => [
  {
    name: 'date',
    label: 'Millésime déclaration',
    inputType: 'date',
    type: 'date',
    format: 'YYYYMM',
    isFilter: true,
    operators: dateOperators
  },
  {
    name: 'CA_total',
    label: 'CA',
    component: (v) => <Typography>{`${v} €`}</Typography>,
    isFilter: true,
    operators: dateOperators
  },
  {
    name: 'tax_total',
    label: 'Cotisations totales',
    component: (v) => <Typography>{`${v} €`}</Typography>,
    isFilter: true,
    operators: dateOperators
  },
  {
    name: 'is_payed',
    label: 'Payées',
    isFilter: true,
    valueEditorType: 'multiselect',
    values: yesNo,
    mapping: (id) => yesNo.find((i) => i.id === id)?.label || id,
    component: (v) => <Checkbox checked={!!v} disabled />,
    operators: [...equalityOperators, ...multiselectOperators]
  },
  {
    name: 'payment_date',
    label: 'Date paiement',
    inputType: 'date',
    type: 'date',
    format: 'DD/MM/YYYY',
    isFilter: true,
    operators: dateOperators
  },
  {
    name: 'createdDateTime',
    label: 'Date Création',
    isFilter: true,
    inputType: 'datetime-local',
    type: 'date',
    format: 'DD/MM/YYYY HH:mm:ss',
    operators: dateOperators
  },
  { name: 'createdBy', label: 'Créé Par', isFilter: true, operators: textOperators },
  {
    name: 'lastModDateTime',
    label: 'Date Modification',
    isFilter: true,
    inputType: 'datetime-local',
    type: 'date',
    format: 'DD/MM/YYYY HH:mm:ss',
    operators: dateOperators
  },
  { name: 'lastModBy', label: 'Modifié Par', isFilter: true, operators: textOperators }
]

export const getTaxFields = () => [
  [
    {
      name: 'date',
      label: 'Millésime déclaration',
      type: 'date',
      views: ['month', 'year'],
      required: true,
      fullWidth: true,
      validation: [{ rule: 'req', error: 'Millésime déclaration obligatoire' }]
    },
    {
      name: 'description',
      label: 'Description',
      type: 'text',
      fullWidth: true,
      isDisplay: ({ date }) => !!date,
      readOnly: true
    }
  ],
  [
    {
      name: 'CA_selling',
      label: 'CA ventes de marchandise',
      type: 'currency',
      readOnly: true,
      fullWidth: true,
      isDisplay: ({ date }) => !!date
    },
    {
      name: 'CA_service',
      label: 'CA prestations services commerciales ou artisanales',
      type: 'currency',
      readOnly: true,
      fullWidth: true,
      isDisplay: ({ date }) => !!date
    },
    {
      name: 'CA_other',
      label: 'CA autres prestations de services',
      type: 'currency',
      readOnly: true,
      fullWidth: true,
      isDisplay: ({ date }) => !!date
    },
    {
      name: 'CA_total',
      label: 'CA total',
      type: 'currency',
      readOnly: true,
      fullWidth: true,
      isDisplay: ({ date }) => !!date
    }
  ],
  [
    {
      name: 'tax_selling',
      label: 'Cotisations vente de marchandises (BIC)',
      type: 'currency',
      readOnly: true,
      fullWidth: true,
      isDisplay: ({ date }) => !!date
    },
    {
      name: 'tax_service',
      label: 'Cotisations prestations de services (BIC)',
      type: 'currency',
      readOnly: true,
      fullWidth: true,
      isDisplay: ({ date }) => !!date
    },
    {
      name: 'tax_other',
      label: 'Cotisations prestations de services (BNC)',
      type: 'currency',
      readOnly: true,
      fullWidth: true,
      isDisplay: ({ date }) => !!date
    },
    {
      name: 'tax_training',
      label: 'Formation prof. libérale obligatoire',
      type: 'currency',
      readOnly: true,
      fullWidth: true,
      isDisplay: ({ date }) => !!date
    }
  ],
  [
    {
      name: 'tax_total',
      label: 'Montant total cotisations',
      type: 'currency',
      readOnly: true,
      fullWidth: true,
      isDisplay: ({ date }) => !!date
    }
  ],
  [
    {
      name: 'is_payed',
      label: 'Payé',
      type: 'checkbox',
      fullWidth: true,
      isDisplay: ({ date }) => !!date
    }
  ],
  [
    {
      name: 'payment_date',
      label: 'Date paiement',
      type: 'date',
      required: (data) => data.is_payed === true,
      fullWidth: true,
      validation: [{ rule: 'req', error: 'Date paiement obligatoire' }],
      isDisplay: ({ is_payed }) => !!is_payed
    }
  ]
]

/** -------------------- ACCOUNTING -------------------- */
export const accountingColums = [
  { name: 'periodYear', label: 'Année', onlyHeader: true, isFilter: true, operators: dateOperators },
  { name: 'periodYearMonth', label: 'Mois', onlyHeader: true, isFilter: true, operators: dateOperators },
  {
    name: 'date',
    label: 'Date',
    inputType: 'date',
    type: 'date',
    format: 'DD/MM/YYYY',
    isFilter: true,
    operators: dateOperators
  },
  { name: 'description', label: 'Description', type: 'text', isSortable: false },
  { name: 'credit', label: 'Crédit', type: 'currency', isSortable: false },
  { name: 'debit', label: 'Débit', type: 'currency', isSortable: false }
]
